import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import Layout from "../components/layout/Layout";

// Collection categories for home preview
const featuredCategories = [
  {
    id: 1,
    name: "Men's Wear",
    description: "Refined elegance for the modern gentleman",
    image:
      "/mens-shirt/shirt-6.jpeg",
  },
  {
    id: 2,
    name: "Women's Wear",
    description: "Timeless grace for every occasion",
    image:
      "/ladies-wear/salwar_3.jpeg",
  },
  {
    id: 3,
    name: "Ethnic & Traditional",
    description: "Celebrating heritage with artisan craft",
    image:
      "/ladies-wear/top_12.jpeg",
  },
];

// Trust indicators
const trustPoints = [
  { number: "30+", label: "Years of Trust" },
  { number: "5 Lakh+", label: "Happy Families" },
  { number: "500+", label: "Wedding Collections" },
];

export const Home = () => {
  const heroRef = useRef(null);
  const categoriesRef = useRef(null);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      "Hello, I would like to enquire about a design from Anand Fashion.",
    );
    window.open(`https://wa.me/919214456664?text=${message}`, "_blank");
  };

  // Intersection Observer for reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 },
    );

    const revealElements = document.querySelectorAll(".reveal-section");
    revealElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Layout>
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="/image/Anand FASHION Banner.png"
            alt="Anand Fashion Heritage"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background" />
          <div className="absolute inset-0 bg-ivory/30" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container-heritage text-center pt-24">
          <div className="max-w-3xl mx-auto stagger-children">
            {/* Decorative element */}
            <div className="flex justify-center mb-8">
              <div className="line-gold" />
            </div>

            {/* Main heading */}
            <h1 className="heading-display text-foreground mb-6">
              Anand Fashion
            </h1>

            {/* Subheadline */}
            <p className="font-serif text-xl md:text-2xl text-foreground/80 italic mb-4">
              Three Decades of Trusted Style Store in Udaipur for Men, Women &
              Premium Wear
            </p>

            {/* Supporting line */}
            <p className="body-large max-w-xl mx-auto mb-10">
              Curated fashion for every generation and every occasion
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
              <Button
                onClick={handleWhatsAppClick}
                className="btn-heritage-filled group min-w-[200px]"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Enquire on WhatsApp
              </Button>
              <Link to="/collection">
                <Button
                  variant="ghost"
                  className="btn-heritage min-w-[200px] group"
                >
                  View Collection
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-fade-in"
          style={{ animationDelay: "1s" }}
        >
          <span className="text-xs uppercase tracking-widest text-muted-foreground">
            Explore
          </span>
          <div className="w-px h-8 bg-gradient-to-b from-muted-foreground/50 to-transparent" />
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 md:py-20 bg-secondary/50">
        <div className="container-heritage">
          <div className="grid grid-cols-3 gap-6 md:gap-12">
            {trustPoints.map((point, index) => (
              <div
                key={index}
                className="text-center reveal-section"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <p className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-2">
                  {point.number}
                </p>
                <p className="text-xs md:text-sm uppercase tracking-wider text-muted-foreground">
                  {point.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Categories Preview */}
      <section ref={categoriesRef} className="section-padding">
        <div className="container-heritage">
          {/* Section Header */}
          <div className="text-center mb-16 md:mb-20 reveal-section">
            <span className="label-uppercase mb-4 block">Our Branded Collection</span>
            <h2 className="heading-section text-foreground mb-6">
              Curated for Excellence
            </h2>
            <div className="flex justify-center">
              <div className="line-gold" />
            </div>
          </div>

          {/* Categories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {featuredCategories.map((category, index) => (
              <Link
                key={category.id}
                to="/collection"
                className="group reveal-section gallery-card"
                style={{ transitionDelay: `${index * 0.15}s` }}
              >
                <div className="relative aspect-[3/4] overflow-hidden rounded-sm image-overlay-hover">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-700 ease-heritage group-hover:scale-105"
                  />
                  <div className="image-overlay" />

                  {/* Content */}
                  <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                    <h3 className="font-serif text-xl md:text-2xl text-foreground mb-2 transition-colors duration-300 group-hover:text-foreground">
                      {category.name}
                    </h3>
                    <p className="text-sm text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      {category.description}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* View All CTA */}
          <div className="text-center mt-12 md:mt-16 reveal-section">
            <Link to="/collection">
              <Button variant="ghost" className="btn-heritage group">
                View Full Collection
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Heritage Story Preview */}
      <section className="section-padding bg-gradient-subtle">
        <div className="container-heritage">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <div className="reveal-section order-2 lg:order-1">
              <div className="relative aspect-[4/5] overflow-hidden rounded-sm">
                <img
                    src="/anandfashionshop.png"
                    alt="Anand Fashion Showroom Interior"
                    className="w-full h-full object-cover"
                  />
                {/* Decorative frame */}
                <div className="absolute inset-4 border border-gold/20 pointer-events-none rounded-sm" />
              </div>
            </div>

            {/* Content */}
            <div className="reveal-section order-1 lg:order-2">
              <span className="label-uppercase mb-4 block">Our Story</span>
              <h2 className="heading-section text-foreground mb-6">
                A Legacy Built on Trust
              </h2>
              <div className="line-gold-short mb-8" />
              <div className="space-y-5">
                <p className="body-large">
                  For over three decades, Anand Fashion has been more than a
                  showroom — it has been a trusted destination for families
                  seeking quality, craftsmanship, and timeless style.
                </p>
                <p className="body-large">
                  Anand Fashion is a well-known clothing store in Udaipur
                  offering a wide range of men’s wear, women’s wear and premium
                  clothing. Located in Bhopalpura near Gattani Hospital, our
                  store has been serving local families for over a decade with
                  quality fabrics and latest fashion trends. From stylish
                  shirts, jeans and winter wear for men to elegant suits, kurtis
                  and co-ord sets for women, Anand Fashion is a trusted choice
                  for everyday wear as well as special occasions. Our premium
                  collection includes blazers, sherwani and party wear suitable
                  for weddings and celebrations in Udaipur.
                </p>
              </div>
              <div className="mt-10">
                <Link to="/about">
                  <Button variant="ghost" className="btn-heritage group">
                    Learn Our Story
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding-sm">
        <div className="container-heritage">
          <div className="bg-secondary/70 rounded-sm p-10 md:p-16 text-center reveal-section">
            <h2 className="heading-subsection text-foreground mb-4">
              Visit Our Showroom
            </h2>
            <p className="body-large max-w-lg mx-auto mb-8">
              Experience our Branded Collection in person. Our fashion consultants are
              ready to help you find the perfect style.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={handleWhatsAppClick}
                className="btn-heritage-filled group min-w-[200px]"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Enquire Now
              </Button>
              <Link to="/contact">
                <Button variant="ghost" className="btn-heritage min-w-[200px]">
                  Get Directions
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
